/**
 * HowToPlay — builder for the "How to Play" wiki overlay.
 *
 * Pure DOM builder: given the TOC + content containers it renders the whole
 * page (sections, mini cards, tables, illustrations). All GAME DATA is
 * derived from the shared single sources of truth — the same convention as
 * KeywordsOverlay — so the page can never drift from the rules:
 *   - starter card pool      → testMinions + UNDEAD_MINION_DEFINITION
 *   - class name/color/icon  → MINION_TYPE_* display constants
 *   - rule numbers           → GameConfig + getPreparationTimer
 *   - combat outcome rules   → CombatService gold/life constants
 *   - rune catalogue         → RUNE_DEFINITIONS (art + cost + description)
 *   - keyword trigger types  → VALID_TRIGGER_TYPES
 *   - base ability text      → GAME_ABILITY_DEFINITIONS
 * Only the explanatory prose itself is static.
 *
 * Purely client-side (no server round-trip) — like the keyword overlay it is
 * available to EVERYONE, guests included (the button lives on the landing
 * screen, visible in every auth subview and the queue view).
 */

import { testMinions, UNDEAD_MINION_DEFINITION } from '../../shared/models/testMinions';
import type { MinionDefinition } from '../../shared/models/MinionDefinition';
import {
  MINION_TYPE_NAMES,
  MINION_TYPE_COLORS,
  MINION_TYPE_ICONS,
  MinionType,
} from '../../shared/models/MinionType';
import { GameConfig, getPreparationTimer } from '../../config/gameConfig';
import {
  RUNE_DEFINITIONS,
  HIGH_STAKES_STAT_BONUS,
  HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL,
} from '../../shared/models/RuneDefinitions';
import { VALID_TRIGGER_TYPES } from '../../shared/models/AbilityRegistry';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';
import {
  LOSS_LIFE_PENALTY,
  LOSS_GOLD_REWARD,
  TIE_GOLD_REWARD,
  TIMEOUT_LIFE_PENALTY,
  TIMEOUT_GOLD_REWARD,
  SIT_OUT_GOLD_REWARD,
} from '../../services/CombatService';

/** Minimal structural view of an ability definition (name + description). */
interface AbilityInfo {
  name: string;
  description: string;
}

/** id → {name, description} for every keyword (resolved once). */
const ABILITY_BY_ID: ReadonlyMap<string, AbilityInfo> = new Map(
  GAME_ABILITY_DEFINITIONS.map((def) => [def.id, { name: def.name, description: def.description }])
);

/** Escapes a string for safe interpolation into HTML. */
function esc(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Class card-art path: the display name is the file name convention
 * (/card-art/Warrior.png, /card-art/Tank.png, … — the same mapping the
 * in-game MinionCard uses). Unknown types get no art.
 */
function classArtPath(minionType: string): string | null {
  const type = minionType as MinionType;
  const name = MINION_TYPE_NAMES[type];
  return name ? `/card-art/${esc(name)}.png` : null;
}

/**
 * Renders one compact wiki card (art, class badge, level, stats, base
 * keywords) for a minion definition. `token` renders the dashed "TOKEN"
 * variant.
 */
function miniCardHtml(def: MinionDefinition, opts: { token?: boolean } = {}): string {
  const typeKey = def.minionType as MinionType;
  const className = MINION_TYPE_NAMES[typeKey] ?? def.minionType;
  const color = MINION_TYPE_COLORS[typeKey] ?? '#888';
  const icon = MINION_TYPE_ICONS[typeKey] ?? '❓';
  const art = classArtPath(def.minionType);
  const abilities = def.baseAbilities
    .map((id) => ABILITY_BY_ID.get(id))
    .filter((a): a is AbilityInfo => a !== undefined);
  const abilitiesHtml =
    abilities.length > 0
      ? `<div class="htp-card-ab" title="${esc(abilities.map((a) => a.description).join('; '))}">★ ${abilities
          .map((a) => esc(a.name))
          .join(', ')}</div>`
      : '<div class="htp-card-ab none">No keyword</div>';
  const badgeExtra = opts.token ? ' · TOKEN' : '';
  return `
    <div class="htp-card${opts.token ? ' token' : ''}">
      <div class="htp-card-art">
        ${art ? `<img src="${art}" alt="${esc(className)}" draggable="false">` : ''}
        <span class="htp-card-badge" style="background:${color}">${icon} ${esc(className)}${badgeExtra}</span>
        <span class="htp-card-level">Lv.1</span>
      </div>
      <div class="htp-card-name">${esc(def.name)}</div>
      <div class="htp-card-stats">
        <span class="htp-stat atk" title="Attack">⚔ ${def.attack}</span>
        <span class="htp-stat hp" title="Health">❤ ${def.startingHp}</span>
      </div>
      ${abilitiesHtml}
    </div>`;
}

/** One-line explanations for the five keyword trigger types. */
const TRIGGER_NOTES: Record<string, string> = {
  OnDraw: 'when the card is drawn into a hand',
  OnCombatStart: 'once, before the first attack of the combat',
  OnAttack: 'when the minion attacks',
  OnKill: 'when the minion lands a killing blow',
  OnDeath: 'when the minion dies',
};

/** A section of the wiki page: id (anchor), TOC label, HTML body. */
interface HowToPlaySection {
  id: string;
  label: string;
  html: string;
}

/** Builds every section of the page (single source for the TOC too). */
function buildSections(): HowToPlaySection[] {
  const timerEarly = getPreparationTimer(1);
  const timerMid = getPreparationTimer(4);
  const timerLate = getPreparationTimer(8);

  const sections: HowToPlaySection[] = [];

  // ── 1. Overview ──────────────────────────────────────────────────────
  sections.push({
    id: 'htp-overview',
    label: 'Overview',
    html: `
      <p class="htp-p">BattleBrew is a <b>${GameConfig.playerCount}-player auto-battler</b>. You
      never fight directly: each round you spend a <b>preparation</b> phase building your board,
      then the server pairs you with an opponent and your minions fight <b>automatically</b>.
      The last player standing wins.</p>
      <p class="htp-p">You start each match with <b>${GameConfig.startingLife} ❤ life</b>,
      <b>${GameConfig.startingGold} 💰 gold</b> and a hand of <b>${GameConfig.startingHandSize} cards</b>.
      Every action — play, sell, upgrade, buy — is validated by the server; the browser only
      shows what the server decides.</p>`,
  });

  // ── 2. Game loop ─────────────────────────────────────────────────────
  sections.push({
    id: 'htp-loop',
    label: 'Game Loop',
    html: `
      <ol class="htp-loop">
        <li><span class="htp-loop-num">1</span><div class="htp-loop-body"><b>Join the queue</b> —
          press <b>Join Queue</b> on the landing page. The match starts when
          ${GameConfig.playerCount} players are in; if nobody joins within 30s, <b>bots fill the
          remaining seats</b>, so the game always starts.</div></li>
        <li><span class="htp-loop-num">2</span><div class="htp-loop-body"><b>Prepare</b> — the round
          timer starts (${timerEarly}s / ${timerMid}s / ${timerLate}s, tiered by round). You draw a card
          automatically, then buy, play, move, sell and upgrade minions.</div></li>
        <li><span class="htp-loop-num">3</span><div class="htp-loop-body"><b>Ready</b> — press the
          <b>Ready</b> plate when you are done. Combat starts immediately once <b>every</b> player
          is ready. Otherwise, when the timer hits <b>0</b> your actions lock and combat begins a
          short ${GameConfig.preparationLockThreshold}s later.</div></li>
        <li><span class="htp-loop-num">4</span><div class="htp-loop-body"><b>Combat</b> — you are
          randomly paired (rematches are avoided). With an odd number of players the
          lowest-life player <b>sits out</b> and spectates the fight.</div></li>
        <li><span class="htp-loop-num">5</span><div class="htp-loop-body"><b>Results</b> — losing a
          combat costs ${LOSS_LIFE_PENALTY} ❤ life (and pays a small gold refund); at 0 life you are
          <b>eliminated</b>. Kill gold and other rewards are banked.</div></li>
        <li><span class="htp-loop-num">6</span><div class="htp-loop-body"><b>Repeat</b> — a new round
          begins. Survive until you are the last player standing; your rating (the <b>Top 100</b>)
          is updated after every decisive combat.</div></li>
      </ol>`,
  });

  // ── 3. Preparation phase ─────────────────────────────────────────────
  const byId = (id: string): MinionDefinition | undefined => testMinions.find((m) => m.id === id);
  const prepFieldCards = ['tank', 'warrior', 'mage']
    .map(byId)
    .filter((m): m is MinionDefinition => m !== undefined)
    .map((m) => miniCardHtml(m))
    .join('');
  const prepHandCards = ['bard', 'healer']
    .map(byId)
    .filter((m): m is MinionDefinition => m !== undefined)
    .map((m) => miniCardHtml(m))
    .join('');

  sections.push({
    id: 'htp-prep',
    label: 'Preparation',
    html: `
      <p class="htp-p">The preparation phase is the whole game in miniature — the only time you
      control anything. The timer is tiered by round: <b>rounds 1–3 → ${timerEarly}s · rounds 4–7 →
      ${timerMid}s · round 8+ → ${timerLate}s</b>. Actions stay available for the whole
      countdown; when it hits <b>0</b> all actions lock and combat begins a short
      ${GameConfig.preparationLockThreshold}s later.</p>
      <table class="htp-table">
        <tr><th>Action</th><th>How</th><th>Note</th></tr>
        <tr><td><b>Draw a card</b></td><td>Automatic, at the start of each round</td>
          <td>+1 extra per <b>Draw</b> minion on your board · hand cap ${GameConfig.maxHandSize}</td></tr>
        <tr><td><b>Buy a card</b></td><td>🎴 <b>Buy Card</b> plate (right of the hand)</td>
          <td>${GameConfig.cardPurchaseCost}g, −1g per <b>Discount</b> minion on the board (never below
          ${GameConfig.cardPurchaseMinCost}g) — a random one of the 5</td></tr>
        <tr><td><b>Play a card</b></td><td>Drag a hand card onto the battlefield — or
          <b>double-click / double-tap</b> it</td>
          <td>Board cap ${GameConfig.maxBoardSize}</td></tr>
        <tr><td><b>Reposition</b></td><td>Drag a minion along the battlefield</td>
          <td>Order matters: many keywords target a specific neighbour</td></tr>
        <tr><td><b>Sell</b></td><td>Drag a minion to the top edge of the board
          (the sell zone) — both mouse and touch</td>
          <td>Refunds its <b>level</b> in gold (Lv.1 = 1g … Lv.6 = 6g)</td></tr>
        <tr><td><b>Upgrade</b></td><td><b>Double-click</b> a minion on the board</td>
          <td>3 branches, cost grows with level (see <b>Upgrades</b>)</td></tr>
        <tr><td><b>Buy a rune</b></td><td>🪬 <b>Buy Rune</b> (preparation phase) — opens the rune
          shop with <b>every</b> rune and its gold price</td>
          <td>Any rune, once per game — effects stack (see <b>Runes</b>)</td></tr>
        <tr><td><b>Ready</b></td><td>🟢 <b>Ready</b> plate (lower-right of the hand)</td>
          <td>Combat starts the moment everyone is ready — don't wait for the timer</td></tr>
      </table>
      <div class="htp-mini-screen">
        <div class="htp-mini-caption">Your battlefield (up to ${GameConfig.maxBoardSize})</div>
        <div class="htp-mini-battlefield">${prepFieldCards}</div>
        <div class="htp-mini-caption">Your hand (up to ${GameConfig.maxHandSize})</div>
        <div class="htp-mini-hand">
          ${prepHandCards}
          <div class="htp-plate"><img src="/Draw.png" alt="" draggable="false"><span>Buy a card · ${GameConfig.cardPurchaseCost}g</span></div>
          <div class="htp-plate"><img src="/Info.png" alt="" draggable="false"><span>📖 Keywords</span></div>
          <div class="htp-plate"><img src="/UnRdy.png" alt="" draggable="false"><span>Ready</span></div>
        </div>
      </div>`,
  });

  // ── 4. The cards ─────────────────────────────────────────────────────
  sections.push({
    id: 'htp-cards',
    label: 'The Cards',
    html: `
      <p class="htp-p">The whole card pool is <b>exactly ${testMinions.length} cards</b> — one per
      class — and every buy or draw is <b>random with equal odds</b>. You can't pick your deck; you
      build around whatever lands. Each minion starts at <b>Lv.1</b> and can be upgraded to
      Lv.${GameConfig.maxUpgrades + 1}.</p>
      <div class="htp-cards">${testMinions.map((m) => miniCardHtml(m)).join('')}</div>
      <p class="htp-p">There is one more creature in the game — <b>the Undead</b>. It is not part
      of the normal card pool, so it can only be drawn with the right ability: while a
      <b>Gravedigger</b> is on your battlefield the Undead joins your draw pool, and the Rune of
      the Necromancer turns each of your draws into a 50% roll for one. An Undead in your hand
      is a real card — play, sell and upgrade it like any other. The Undead can also appear
      <b>during combat</b>: the <b>Necromancy</b> keyword (or the Rune of the Dead) temporarily
      replaces a dead friendly minion with a living copy, which vanishes when the combat ends.</p>
      <div class="htp-cards">${miniCardHtml(UNDEAD_MINION_DEFINITION)}</div>`,
  });

  // ── 5. Upgrades ──────────────────────────────────────────────────────
  sections.push({
    id: 'htp-upgrades',
    label: 'Upgrades',
    html: `
      <p class="htp-p">During preparation, <b>double-click (double-tap) a minion on your
      battlefield</b> to open the upgrade panel. You always get three choices (the
      <b>Rune of Options</b> adds a fourth):</p>
      <div class="htp-upgrade-mock">
        <div class="htp-upgrade-head">Upgrade <b>Warrior</b> — Lv.2 → Lv.3 · <span class="htp-cost">${GameConfig.upgradeCost + 1}g</span></div>
        <div class="htp-branches">
          <div class="htp-branch"><b>❤ +5 HP</b><small>Health Boost</small></div>
          <div class="htp-branch"><b>⚔ +5 ATK</b><small>Attack Boost</small></div>
          <div class="htp-branch ability"><b>🎲 Gain a keyword</b>
            <small>The server picks one your class &amp; level are eligible for</small></div>
        </div>
      </div>
      <p class="htp-p">Cost: <b>${GameConfig.upgradeCost}g for the first upgrade, +1g per
      level</b> (${GameConfig.upgradeCost}g → ${GameConfig.upgradeCost + GameConfig.maxUpgrades - 1}g). A minion
      takes ${GameConfig.maxUpgrades} upgrades, from Lv.1 to Lv.${GameConfig.maxUpgrades + 1} — and every
      <b>Grandmaster</b> minion on your board raises that cap by +1 for all of your minions.</p>
      <p class="htp-p"><b>Your level is also your sell price.</b> A Lv.4 minion refunds 4g when
      sold, so upgrading is a double investment: more stats <i>and</i> a bigger refund if you trade
      it away later.</p>
      <p class="htp-p"><b>The Rune of High Stakes changes the deal.</b> While you own it, the two
      stat choices are replaced by a single <b>High Stakes</b> option: <b>+${HIGH_STAKES_STAT_BONUS} HP AND
      +${HIGH_STAKES_STAT_BONUS} ATK</b>, with a <b>${Math.round(HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL * 100)}% × level</b> chance to
      <b>destroy the minion</b> instead (Lv.1: ${Math.round(HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL * 100)}%, Lv.2: ${Math.round(HIGH_STAKES_DESTROY_CHANCE_PER_LEVEL * 200)}%, …). A destroyed minion is
      gone for good — the upgrade cost is paid and nothing is refunded. Keyword options are
      unaffected.</p>`,
  });

  // ── 6. Combat ────────────────────────────────────────────────────────
  sections.push({
    id: 'htp-combat',
    label: 'Combat',
    html: `
      <p class="htp-p"><b>Pairing</b> — each round the server pairs the living players at random,
      avoiding rematches when it can. With an odd number of players the lowest-life player
      <b>sits out</b>: they spectate the biggest fight and are paid ${SIT_OUT_GOLD_REWARD}g + the round
      number.</p>
      <p class="htp-p"><b>The fight</b> — the two boards attack in turns. Each attacker picks a
      target — minions with <b>Shield</b> (taunt) must be attacked before anything else — and deals
      damage to its target <b>simultaneously</b> (both hit at once). Keywords trigger on attack,
      kill and death, so Lifesteal, Explosive and friends all play out on their own.</p>
      <p class="htp-p"><b>Kill gold</b> — every enemy minion you kill is worth gold equal to its
      <b>level</b> (a Lv.1 kill = 1g, a Lv.6 kill = 6g).</p>
      <p class="htp-p"><b>Sudden death</b> — after ${GameConfig.suddenDeathAttackThreshold} attacks the
      combat enters sudden death: from the next attack on, <b>every living minion on both sides
      takes escalating extra damage</b> (1, then 2, then 3, …). No fight can drag on forever.</p>
      <p class="htp-p"><b>Timeout</b> — a combat is also capped at ${GameConfig.combatTimeout} seconds in
      real time; if it times out, both players pay for it.</p>
      <table class="htp-table">
        <tr><th>Outcome</th><th>What happens</th></tr>
        <tr><td><b>Decisive win</b></td>
          <td>The loser takes ${LOSS_LIFE_PENALTY} ❤ life and gains ${LOSS_GOLD_REWARD}g; you bank your
          kill gold.</td></tr>
        <tr><td><b>Decisive loss</b></td>
          <td>−${LOSS_LIFE_PENALTY} ❤ life, +${LOSS_GOLD_REWARD}g consolation gold.</td></tr>
        <tr><td><b>Tie</b></td>
          <td>Both players gain ${TIE_GOLD_REWARD}g — no life lost either way.</td></tr>
        <tr><td><b>Timeout</b></td>
          <td>Both players: −${TIMEOUT_LIFE_PENALTY} ❤ life, +${TIMEOUT_GOLD_REWARD}g.</td></tr>
        <tr><td><b>Sit out</b> (odd number of players)</td>
          <td>+${SIT_OUT_GOLD_REWARD}g + the round number, and you spectate.</td></tr>
      </table>`,
  });

  // ── 7. Gold ──────────────────────────────────────────────────────────
  sections.push({
    id: 'htp-gold',
    label: 'Gold',
    html: `
      <p class="htp-p">Gold is the only currency. You start with ${GameConfig.startingGold}g;
      everything else is earned or refunded.</p>
      <table class="htp-table">
        <tr><th>Earn</th><th>Amount</th></tr>
        <tr><td>Sell a minion</td><td>its <b>level</b> in gold (1g–6g)</td></tr>
        <tr><td>Kill an enemy minion in combat</td><td>the victim's <b>level</b> in gold</td></tr>
        <tr><td>Win a combat</td><td>— (the loser pays ${LOSS_GOLD_REWARD}g, but that gold is theirs)</td></tr>
        <tr><td>Lose a combat</td><td>+${LOSS_GOLD_REWARD}g consolation</td></tr>
        <tr><td>Tie a combat</td><td>+${TIE_GOLD_REWARD}g (both players)</td></tr>
        <tr><td>Sit out a round</td><td>+${SIT_OUT_GOLD_REWARD}g + the round number</td></tr>
      </table>
      <table class="htp-table">
        <tr><th>Spend</th><th>Cost</th></tr>
        <tr><td>Buy a card</td><td>${GameConfig.cardPurchaseCost}g (−1g per Discount on the board,
          min ${GameConfig.cardPurchaseMinCost}g)</td></tr>
        <tr><td>Upgrade a minion</td><td>${GameConfig.upgradeCost}g + one per level already bought
          (${GameConfig.upgradeCost}g → ${GameConfig.upgradeCost + GameConfig.maxUpgrades - 1}g)</td></tr>
        <tr><td>Buy a rune</td><td>10–30g (depends on the rune — see <b>Runes</b>)</td></tr>
      </table>`,
  });

  // ── 8. Runes ─────────────────────────────────────────────────────────
  sections.push({
    id: 'htp-runes',
    label: 'Runes',
    html: `
      <p class="htp-p">There is no pre-match rune selection — runes are bought from the
      <b>in-game rune shop</b>. During the preparation phase the 🪬 <b>Buy Rune</b> button opens
      the shop, which lists <b>every rune</b> with its gold price. You can buy <b>any</b> rune —
      each rune is buyable <b>once per game</b> (no duplicates), up to
      <b>${GameConfig.maxRunesPurchasable} runes per game</b>: the button shows your progress as
      <b>(x/N)</b> and the shop locks the remaining runes once you
      reach the cap (your gold is the other limit). A purchased rune's effect activates
      immediately, stays active for the rest of the game, and <b>stacks</b> with your other
      purchased runes. Two battlefield abilities can change the deal: a <b>RuneKeeper</b> in
      play raises the buy limit by one (it stacks, and runes you already bought are kept if
      it later leaves), and a <b>RuneFeast</b> in play makes every rune <b>25% cheaper</b>.
      The 🪬 <b>Runes</b> button on the main menu is a read-only reference to
      this catalogue.</p>
      <div class="htp-rune-grid">
        ${RUNE_DEFINITIONS.map(
          (rune) => `
          <div class="htp-rune">
            <img src="${esc(rune.image)}" alt="${esc(rune.name)}" draggable="false">
            <div class="htp-rune-text">
              <b>${esc(rune.name)} <span class="htp-rune-cost">${rune.goldCost}g</span></b>
              <p>${esc(rune.description)}</p>
            </div>
          </div>`
        ).join('')}
      </div>`,
  });

  // ── 9. Keywords ──────────────────────────────────────────────────────
  sections.push({
    id: 'htp-keywords',
    label: 'Keywords',
    html: `
      <p class="htp-p">Minions learn <b>keywords</b> (abilities) as they are upgraded; some come
      from the start — the Tank's <b>Shield</b>, the Bard's <b>Buff Vitality</b> and the Healer's
      <b>Heal</b>. Every keyword fires on exactly one trigger:</p>
      <div class="htp-triggers">
        ${VALID_TRIGGER_TYPES.map(
          (t) => `
          <div class="htp-trigger"><code>${esc(t)}</code>
            <span>${esc(TRIGGER_NOTES[t] ?? '')}</span></div>`
        ).join('')}
      </div>
      <p class="htp-p">The full keyword list — every effect, which classes can learn it and at
      which level — lives in the <b>📖 Keywords</b> overlay (the Info plate in-game, the Keywords
      button in the queue). Keywords are where the game gets deep; start with the basics above and
      browse the rest as you meet them.</p>`,
  });

  // ── 10. Tips ─────────────────────────────────────────────────────────
  sections.push({
    id: 'htp-tips',
    label: 'Tips',
    html: `
      <ul class="htp-tips">
        <li><b>Shields must be attacked first</b> — put your tanks in front of your glass
          cannons so the Mage behind them gets turns.</li>
        <li><b>Positioning matters</b> — Buff Vitality and Heal affect the neighbour to the
          <i>left</i>, and Blast splashes the target's neighbours. Rearrange before you Ready.</li>
        <li><b>Sell into upgrades</b> — a minion refunds exactly its level in gold, so trade
          early plays (Lv.1 = 1g) and bank the refund for bigger investments.</li>
        <li><b>Save up</b> — a Lv.${GameConfig.maxUpgrades + 1} minion costs
          ${[...Array(GameConfig.maxUpgrades)].reduce((sum, _, i) => sum + GameConfig.upgradeCost + i, 0)}g in
          upgrades total, and runes cost 10–30g. Early rounds are for cheap buys and small
          upgrades.</li>
        <li><b>A tie is not a loss</b> — both players gain ${TIE_GOLD_REWARD}g.</li>
        <li><b>The game always starts</b> — if your queue doesn't fill with humans, bots take the
          remaining seats after 30s.</li>
        <li><b>Watch the SUDDEN DEATH counter</b> (top-right during combat) — once it appears,
          every minion on both sides bleeds more each attack: the fight is winding down.</li>
        <li><b>Reloads are safe</b> — you can refresh the page mid-match and rejoin the same
          game; the server is the source of truth.</li>
      </ul>`,
  });

  return sections;
}

/**
 * Builds the whole "How to Play" page into the given containers (idempotent —
 * a second call rebuilds the same content). The TOC is derived from the same
 * section list as the content, so the nav can never drift from the page.
 *
 * @param toc The (empty) sticky TOC element to fill with section chips.
 * @param content The (empty) scrollable content element to fill with sections.
 */
export function buildHowToPlayContent(toc: HTMLElement, content: HTMLElement): void {
  const sections = buildSections();

  toc.innerHTML = sections
    .map((s) => `<button class="htp-toc-btn" data-target="${s.id}">${esc(s.label)}</button>`)
    .join('');

  content.innerHTML = sections
    .map(
      (s) => `
      <section class="htp-section" id="${s.id}">
        <h3>${esc(s.label)}</h3>
        ${s.html}
      </section>`
    )
    .join('') + `
    <p class="htp-footer">All numbers on this page are generated from the live game data — if a
    rule changed in this alpha, the page changed with it. For every keyword effect, see the 📖
    Keywords overlay.</p>`;

  // TOC navigation: smooth-scroll the section to the top of the body
  // (respecting prefers-reduced-motion). Buttons instead of <a href> so the
  // URL hash is never touched.
  toc.querySelectorAll<HTMLButtonElement>('.htp-toc-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = document.getElementById(btn.dataset.target ?? '');
      if (target) {
        const reduced =
          typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      }
    });
  });
}
